import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_ATTEMPTS = 10;
const INTERVAL = 2000;

export default function BillingSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const whishOrder = sp.get("whish_order");
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId && !whishOrder) { setStatus("error"); return; }
    let attempts = 0;
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      attempts++;
      try {
        if (whishOrder) {
          const { data } = await api.get(`/payments/status/${whishOrder}`);
          setData(data);
          if (data.status === "paid") { setStatus("paid"); refreshUser(); return; }
          if (["failed", "cancelled"].includes(data.status)) { setStatus("expired"); return; }
        } else {
          const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
          setData(data);
          if (data.payment_status === "paid") { setStatus("paid"); refreshUser(); return; }
          if (data.status === "expired") { setStatus("expired"); return; }
        }
      } catch (err) {
        console.warn(`Billing status poll attempt ${attempts} failed; retrying`, err);
      }
      if (attempts >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
      setTimeout(poll, INTERVAL);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, whishOrder, refreshUser]);

  return (
    <div className="p-8 max-w-2xl mx-auto pt-20">
      <div className="kr-card p-10 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-[#00E5FF]" />
            <h1 className="font-display text-2xl mt-6">Confirming your payment…</h1>
            <p className="text-white/55 text-sm mt-2">This usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 mx-auto text-[#34D399]" />
            <h1 className="font-display text-3xl mt-6">Welcome to {data?.plan}.</h1>
            <p className="text-white/55 text-sm mt-2">Your credits have been added. Go build something incredible.</p>
            <Button onClick={() => nav("/dashboard")} className="mt-8 bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 rounded-full px-6" data-testid="success-continue-btn">Continue to dashboard</Button>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <XCircle className="h-12 w-12 mx-auto text-red-400" />
            <h1 className="font-display text-2xl mt-6">Payment not confirmed.</h1>
            <p className="text-white/55 text-sm mt-2">If you completed payment, refresh in a moment. Otherwise try again.</p>
            <Button onClick={() => nav("/billing")} className="mt-8 bg-white/5 hover:bg-white/10 rounded-full px-6" data-testid="success-back-btn">Back to billing</Button>
          </>
        )}
      </div>
    </div>
  );
}
